import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { isEmpty } from "lodash";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CheckboxMultiSelect,
  Input,
  Modal,
  Select,
} from "@/components";
import { BusinessTypeEnum, CreateUserDto, RoleEnum } from "@/types";
import { passwordValidation, phoneNumberValidation, REQUIRED_MESSAGE } from "../company/schema";
import { useCompanyStore } from "@/store/companyStore";
import { useGroupStore } from "@/store/groupStore";
import { useSearchParams } from "react-router-dom";
import { KEYS_SEARCH_PARAMS } from "./CompanyDetail";
import { filterParams } from "@/utils/filterParams";
import { useUserStore } from "@/store/userStore";
import { useLoadingStore } from "@/store/loadingStore";
import { useAuth } from "@/contexts/AuthContext";
import { toHalfWidth } from "@/utils/formatCodeNumber";
import { ROLE_LABELS } from "@/utils/constants";

type CreateUserFormData = {
  role?: RoleEnum;
  name: string;
  salesOffice: string;
  position: string;
  department?: string;
  phone?: string;
  email: string;
  password: string;
  group?: number[];
};

type CreateUserFormDataNonMember = {
  name: string;
  email: string;
  password: string;
};

const LIMIT = 20;
const FIRST_PAGE = 1;
const OptionsPosition = [
  { label: ROLE_LABELS.admin, value: RoleEnum.COMPANY_ADMIN },
  { label: ROLE_LABELS.head, value: RoleEnum.HEAD_OFFICE_USER },
];

const schema = z.object({
  role: z.enum(RoleEnum),
  name: z.string().trim().min(1, REQUIRED_MESSAGE),
  salesOffice: z.string().trim().min(1, REQUIRED_MESSAGE),
  position: z.string().trim().min(1, REQUIRED_MESSAGE),
  department: z.string().optional(),
  phone: phoneNumberValidation.optional(),
  email: z.string().min(1, REQUIRED_MESSAGE).trim().email({ message: "メールアドレスは無効な形式です。" }),
  password: passwordValidation,
  group: z.array(z.number()).optional(),
});

const schemaNonMember = z.object({
  name: z.string().min(1, REQUIRED_MESSAGE),
  email: z.string().min(1, REQUIRED_MESSAGE).trim().email({ message: "メールアドレスは無効な形式です。" }),
  password: passwordValidation,
});

export const CreateUser = ({ onDone }: { onDone: () => void }) => {
  const [searchParams] = useSearchParams();
  const { role } = useAuth();

  const [open, setOpen] = useState(false);
  const [loadingGroupsInCompany, setLoadingGroupsInCompany] = useState(false);

  const { company, fetchUsers } = useCompanyStore();
  const { groupsInCompany, fetchGroupsInCompany } = useGroupStore();
  const { createUser } = useUserStore();
  const { setLoading } = useLoadingStore();

  const companyId = Number(company?.id);
  const isCompanyMember = company?.business_type === BusinessTypeEnum.MEMBER;
  const page = groupsInCompany.pagination?.page || 1;
  const hasMore = false; // Update loading inscroll sang load hết;
  const searchUserParam = Object.fromEntries([...searchParams])[
    KEYS_SEARCH_PARAMS.SEARCH_USER
  ];

  const OptionsRole = useMemo(() => {
    if (role === RoleEnum.COMPANY_ADMIN) {
      return OptionsPosition;
    }
    return [];
  }, [role]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    setError,
    reset,
  } = useForm<CreateUserFormData | CreateUserFormDataNonMember>({
    resolver: zodResolver(isCompanyMember ? schema : schemaNonMember),
    defaultValues: {
      role: RoleEnum.HEAD_OFFICE_USER,
    },
  });

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    reset();
  };

  const onSubmit = async (
    data: CreateUserFormDataNonMember | CreateUserFormData
  ) => {
    setLoading(true);
    // data for user of company non_member
    const user: Partial<CreateUserDto> = {
      full_name: data.name?.trim(),
      email: data.email?.trim(),
      password: data.password,
      role: RoleEnum.OTHER,
      company_id: companyId,
    };
    // data for user of company member
    if (isCompanyMember) {
      const memberData = data as CreateUserFormData;
      user.role = memberData.role as any;
      user.sale_office = memberData.salesOffice?.trim();
      user.position = memberData.position?.trim();
      user.department = memberData.department?.trim();
      user.phone = memberData.phone;
      user.store_id = undefined;
      user.group_ids = memberData.group;
    }

    await createUser(filterParams(user))
      .then(() => {
        setLoading(false);
        closeModal();
        fetchUsers(
          companyId,
          filterParams({
            page: 1,
            s: searchUserParam,
            limit: 30,
          })
        );
        onDone?.();
      })
      .catch((error) => {
        if (error?.error) {
          if (error.error == "email") {
            setError("email", { message: error?.message });
          }
        }
        setLoading(false);
      });
  };

  const loadOptions = async (page: number) => {
    if (loadingGroupsInCompany || !companyId) return;
    setLoadingGroupsInCompany(true);
    await fetchGroupsInCompany(role || RoleEnum.HEAD_OFFICE_USER, {
      companyId: companyId,
      page: page,
      limit: LIMIT,
      isPagination: 0, // update loading inscroll sang load hết
    });
    setLoadingGroupsInCompany(false);
  };

  const handleScrollToBottom = () => {
    if (hasMore) {
      const nextPage = page + 1;
      loadOptions(nextPage);
    }
  };

  useEffect(() => {
    if (isEmpty(groupsInCompany.data)) {
      loadOptions(FIRST_PAGE);
    }
  }, [companyId]);

  const storeMap = useMemo(() => {
    return new Map(
      (groupsInCompany?.data || []).map((store: any) => [store.value, store])
    );
  }, [groupsInCompany?.data]);

  const currentRole = watch("role");

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={openModal}
        disabled={!companyId || company?.status == "DISABLE"}
      >
        新規作成
      </Button>
      <Modal
        title={isCompanyMember ? "ユーザー作成" : "事業所ユーザー"}
        open={open}
        onOpenChange={setOpen}
      >
        <form noValidate id="create-user-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-2 gap-4">
            {isCompanyMember && (
              <Controller
                control={control}
                name="role"
                render={({ field: { value, onChange } }) => {
                  const selectedRole = OptionsRole.find(
                    (role) => role.value === value
                  );
                  return (
                    <Select
                      label="役割"
                      placeholder="役割を選択してください"
                      className="col-span-2"
                      value={selectedRole}
                      onChange={(item) => {
                        onChange(item?.value as RoleEnum);
                        setValue("group", undefined);
                      }}
                      options={OptionsRole}
                    />
                  );
                }}
              />
            )}
            {isCompanyMember && currentRole === RoleEnum.HEAD_OFFICE_USER && (
              <Controller
                control={control}
                name="group"
                render={({ field: { value, onChange } }) => {
                  const selectedStores =
                    value && value?.length > 0
                      ? value
                        .map((storeValue) => storeMap.get(storeValue))
                        .filter(Boolean)
                      : [];
                  return (
                    <CheckboxMultiSelect
                      label="グループ"
                      placeholder="グループを選択してください"
                      className="col-span-2"
                      value={selectedStores as any}
                      onChange={(data) => {
                        const values = data
                          ? data.map((item) => item.value)
                          : [];
                        onChange(values);
                      }}
                      options={groupsInCompany.data}
                      isLoading={loadingGroupsInCompany}
                      onMenuScrollToBottom={handleScrollToBottom}
                    />
                  );
                }}
              />
            )}
            <Input
              label="ユーザー名"
              required
              placeholder="ユーザー名を入力してください。"
              maxLength={150}
              containerClassName="col-span-2"
              error={errors.name?.message}
              {...register("name")}
            />
            {isCompanyMember && (
              <>
                <Input
                  label="営業所"
                  required
                  placeholder="営業所を入力してください。"
                  containerClassName="col-span-2"
                  error={(errors as any)?.salesOffice?.message}
                  {...register("salesOffice")}
                />
                <Input
                  label="役職"
                  required
                  placeholder="役職を入力してください。"
                  error={(errors as any)?.position?.message}
                  {...register("position")}
                />
                <Input
                  label="部署"
                  placeholder="部署を入力してください。"
                  error={(errors as any)?.department?.message}
                  {...register("department")}
                />
              </>
            )}
            <Input
              label="メールアドレス"
              required
              placeholder="メールアドレスを入力してください。"
              containerClassName="col-span-2"
              error={errors.email?.message}
              {...register("email")}
            />
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, value } }) => (
                <Input
                  label="パスワード"
                  required
                  placeholder="最低8文字、かつ少なくとも1文字のアルファベットを含む"
                  containerClassName="col-span-2"
                  error={errors.password?.message}
                  value={value || ""}
                  maxLength={150}
                  onChange={onChange}
                  onBlur={() => {
                    onChange(toHalfWidth(value));
                  }}
                />
              )}
            />
          </div>
          <div className="flex justify-end gap-4 mt-4">
            <Button
              buttonType="text"
              type="button"
              className="!h-12"
              onClick={closeModal}
            >
              キャンセル
            </Button>

            <Button
              buttonType="primary"
              type="submit"
              className="!h-12"
              disabled={!isEmpty(errors)}
            >
              作成
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};
